import { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { usePlan } from "@/contexts/PlanContext";
import Logo from "@/components/Logo";
import TileSphereLogo from "@/components/TileSphereLogo";
import NotificationBell from "@/components/NotificationBell";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import {
  LayoutDashboard, Boxes, FileText, Factory, Palette, LogOut, KeyRound, Users, Building2, ShoppingBag, UserPlus,
  ClipboardList, ShoppingCart, PackageCheck, GavelIcon, ShieldCheck, BookOpen, Settings, Inbox,
  UserCog, Lock, Monitor, History,
  CreditCard, Sparkles, LifeBuoy, Activity, Bell, BarChart3, Newspaper, Plug, Database, Tag, Truck, Wallet,
  PackageOpen, ArrowRightLeft, Wrench, ScanBarcode, Layers, Megaphone, Store, ScrollText, Menu, Gift, Network, Mail,
} from "lucide-react";

function resolveLogo(url) {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  if (url.startsWith("/api")) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return url;
}

const vendorLinks = [
  { to: "/vendor", icon: LayoutDashboard, label: "Overview", testid: "nav-vendor-overview", end: true },
  { to: "/vendor/branding", icon: Palette, label: "Profile & Brand", testid: "nav-vendor-branding" },
  { to: "/vendor/products", icon: Boxes, label: "Products", testid: "nav-vendor-products" },
  { to: "/vendor/stock", icon: Factory, label: "Stock & warehouse", testid: "nav-vendor-stock" },
  { to: "/vendor/rfq", icon: FileText, label: "RFQ & Bidding", testid: "nav-vendor-rfq" },
  { to: "/vendor/purchase-orders", icon: ShoppingCart, label: "Buyer orders", testid: "nav-vendor-po" },
  { to: "/vendor/customers", icon: Users, label: "Customers", testid: "nav-vendor-customers" },
  { to: "/vendor/grn", icon: PackageCheck, label: "GRN", testid: "nav-vendor-grn" },
  { to: "/vendor/shipments", icon: Truck, label: "Shipments", testid: "nav-vendor-shipments" },
  { to: "/vendor/pricing", icon: Tag, label: "Pricing & credit", testid: "nav-vendor-pricing" },
  { to: "/vendor/logistics", icon: Truck, label: "Logistics", testid: "nav-vendor-logistics" },
  { to: "/vendor/payments", icon: CreditCard, label: "Payments", testid: "nav-vendor-payments" },
  { to: "/vendor/receipts", icon: Wallet, label: "Receipts", testid: "nav-vendor-receipts" },
  { to: "/vendor/bi", icon: BarChart3, label: "Business intelligence", testid: "nav-vendor-bi" },
  { to: "/vendor/staff", icon: UserPlus, label: "Team & staff", testid: "nav-vendor-staff" },
];
const buyerLinks = [
  { to: "/buyer", icon: ShoppingBag, label: "Marketplace", testid: "nav-buyer-marketplace", end: true },
  { to: "/buyer/rfq", icon: FileText, label: "My RFQs", testid: "nav-buyer-rfq" },
  { to: "/buyer/orders", icon: Factory, label: "My Orders", testid: "nav-buyer-orders" },
];
const adminLinks = [
  { to: "/admin", icon: LayoutDashboard, label: "Dashboard", labelKey: "nav.admin.dashboard", testid: "nav-admin-overview", end: true },
  { to: "/admin/organizations", icon: Building2, label: "Organizations", labelKey: "nav.admin.organizations", testid: "nav-admin-tenants" },
  { to: "/admin/vendors", icon: ShoppingBag, label: "Vendors", labelKey: "nav.admin.vendors", testid: "nav-admin-vendors" },
  { to: "/admin/distributors", icon: Network, label: "Distributors", labelKey: "nav.admin.distributors", testid: "nav-admin-distributors" },
  { to: "/admin/subscriptions", icon: CreditCard, label: "Subscriptions", labelKey: "nav.admin.subscriptions", testid: "nav-admin-subs" },
  { to: "/admin/users", icon: Users, label: "Platform users", labelKey: "nav.admin.platformUsers", testid: "nav-admin-users" },
  { to: "/admin/roles", icon: UserCog, label: "Roles & permissions", labelKey: "nav.admin.rolesPermissions", testid: "nav-admin-roles" },
  { to: "/admin/marketplace", icon: ShoppingBag, label: "Marketplace", labelKey: "nav.admin.marketplace", testid: "nav-admin-marketplace" },
  { to: "/admin/product-masters", icon: Tag, label: "Product masters", labelKey: "nav.admin.productMasters", testid: "nav-admin-masters" },
  { to: "/admin/payments", icon: CreditCard, label: "Payments", labelKey: "nav.admin.payments", testid: "nav-admin-payments" },
  { to: "/admin/support", icon: LifeBuoy, label: "Support center", labelKey: "nav.admin.supportCenter", testid: "nav-admin-support" },
  { to: "/admin/monitoring", icon: Activity, label: "Monitoring", labelKey: "nav.admin.monitoring", testid: "nav-admin-monitoring" },
  { to: "/admin/security", icon: Lock, label: "Security", labelKey: "nav.admin.security", testid: "nav-admin-security" },
  { to: "/admin/notifications", icon: Bell, label: "Notifications", labelKey: "nav.admin.notifications", testid: "nav-admin-notifications" },
  { to: "/admin/reports", icon: BarChart3, label: "Reports", labelKey: "nav.admin.reports", testid: "nav-admin-reports" },
  { to: "/admin/cms", icon: Newspaper, label: "CMS", labelKey: "nav.admin.cms", testid: "nav-admin-cms" },
  { to: "/admin/integrations", icon: Plug, label: "Integrations", labelKey: "nav.admin.integrations", testid: "nav-admin-integrations" },
  { to: "/admin/settings", icon: Settings, label: "Global settings", labelKey: "nav.admin.globalSettings", testid: "nav-admin-settings" },
  { to: "/admin/platform/plans", icon: Sparkles, label: "Platform · Plans", labelKey: "nav.admin.platformPlans", testid: "nav-admin-plat-plans" },
  { to: "/admin/platform/trials", icon: Gift, label: "Platform · Free trials", labelKey: "nav.admin.freeTrials", testid: "nav-admin-plat-trials" },
  { to: "/admin/platform/roles", icon: UserCog, label: "Platform · Role engine", labelKey: "nav.admin.platformRoles", testid: "nav-admin-plat-roles" },
  { to: "/admin/platform/hierarchy", icon: Layers, label: "Platform · Hierarchy", labelKey: "nav.admin.platformHierarchy", testid: "nav-admin-plat-hierarchy" },
  { to: "/admin/platform/pricing", icon: Wallet, label: "Platform · Pricing", labelKey: "nav.admin.platformPricing", testid: "nav-admin-plat-pricing" },
  { to: "/admin/platform/leads", icon: Inbox, label: "Platform · Leads", labelKey: "nav.admin.platformLeads", testid: "nav-admin-plat-leads" },
  { to: "/admin/platform/subscriptions", icon: CreditCard, label: "Platform · Subscriptions", labelKey: "nav.admin.platformSubs", testid: "nav-admin-plat-subs" },
  { to: "/admin/platform/registrations", icon: Inbox, label: "Platform · Registrations", testid: "nav-admin-plat-regs" },
  { to: "/admin/platform/payment-gateway", icon: CreditCard, label: "Platform · Payment gateway", testid: "nav-admin-plat-gateway" },
  { to: "/admin/platform/email-settings", icon: Mail, label: "Platform · Email settings", testid: "nav-admin-plat-email" },
  { to: "/admin/platform/email-connections", icon: Mail, label: "Platform · Email connections", testid: "nav-admin-plat-email-conn" },
  { to: "/admin/audit", icon: Database, label: "Audit logs", labelKey: "nav.admin.auditLogs", testid: "nav-admin-audit" },
];
const orgLinks = [
  { to: "/org", icon: LayoutDashboard, label: "Overview", labelKey: "nav.org.overview", testid: "nav-org-overview", end: true, modules: null },
  { to: "/org/purchase", icon: ShoppingCart, label: "Purchase", labelKey: "nav.org.purchase", testid: "nav-org-purchase", modules: ["purchase"] },
  { to: "/org/vendors", icon: Building2, label: "Vendors", labelKey: "nav.org.vendors", testid: "nav-org-vendors", modules: ["purchase"] },
  { to: "/org/customers", icon: Users, label: "Customers", labelKey: "nav.org.customers", testid: "nav-org-customers", modules: ["sales", "finance"] },
  { to: "/org/stock", icon: Factory, label: "Stock & WH", labelKey: "nav.org.stock", testid: "nav-org-stock", modules: ["store", "warehouse"] },
  { to: "/org/qc", icon: ShieldCheck, label: "Quality control", labelKey: "nav.org.qc", testid: "nav-org-qc", modules: ["qc"] },
  { to: "/org/production", icon: Factory, label: "Production", labelKey: "nav.org.production", testid: "nav-org-production", modules: ["production"] },
  { to: "/org/finance", icon: Wallet, label: "Finance", labelKey: "nav.org.finance", testid: "nav-org-finance", modules: ["finance"] },
  { to: "/org/dispatch", icon: Truck, label: "Dispatch", labelKey: "nav.org.dispatch", testid: "nav-org-dispatch", modules: ["dispatch", "warehouse"] },
  { to: "/org/approvals", icon: Inbox, label: "Approvals inbox", labelKey: "nav.org.approvals", testid: "nav-org-approvals", modules: ["purchase"] },
];

// Admin-only governance section (rendered only when user.role === "ORG_ADMIN")
const orgAdminLinks = [
  { to: "/org/users", icon: UserPlus, label: "Users & credentials", labelKey: "nav.org.users", testid: "nav-org-users" },
  { to: "/org/roles", icon: UserCog, label: "Roles & permissions", labelKey: "nav.org.roles", testid: "nav-org-roles" },
  { to: "/org/bulk-upload-permissions", icon: PackageOpen, label: "Bulk upload permissions", labelKey: "nav.org.bulkUpload", testid: "nav-org-bulk-upload" },
  { to: "/org/profile", icon: Building2, label: "Company profile", labelKey: "nav.org.companyProfile", testid: "nav-org-profile" },
  { to: "/org/subscription", icon: CreditCard, label: "Subscription", labelKey: "nav.org.subscription", testid: "nav-org-subscription" },
  { to: "/org/audit-ca", icon: ScrollText, label: "CA Audit", labelKey: "nav.org.auditCa", testid: "nav-org-audit-ca" },
  { to: "/org/security", icon: Lock, label: "Security controls", labelKey: "nav.org.security", testid: "nav-org-security" },
  { to: "/org/sessions", icon: Monitor, label: "Active sessions", labelKey: "nav.org.sessions", testid: "nav-org-sessions" },
  { to: "/org/login-audit", icon: History, label: "Login audit", labelKey: "nav.org.loginAudit", testid: "nav-org-login-audit" },
];

const salesLinks = [
  { to: "/sales", icon: LayoutDashboard, label: "Dashboard", testid: "nav-sales-dashboard", end: true },
  { to: "/sales/leads", icon: Users, label: "Leads", testid: "nav-sales-leads" },
  { to: "/sales/customers", icon: Users, label: "Customers", testid: "nav-sales-customers" },
  { to: "/sales/dealers", icon: Building2, label: "Dealers", testid: "nav-sales-dealers" },
  { to: "/sales/quotations", icon: FileText, label: "Quotations", testid: "nav-sales-quotations" },
  { to: "/sales/orders", icon: ShoppingCart, label: "Orders", testid: "nav-sales-orders" },
  { to: "/sales/products", icon: Tag, label: "Products", testid: "nav-sales-products" },
  { to: "/sales/inventory", icon: Boxes, label: "Inventory", testid: "nav-sales-inventory" },
  { to: "/sales/visits", icon: Monitor, label: "Visits", testid: "nav-sales-visits" },
  { to: "/sales/tasks", icon: ClipboardList, label: "Tasks", testid: "nav-sales-tasks" },
  { to: "/sales/targets", icon: Inbox, label: "Targets", testid: "nav-sales-targets" },
  { to: "/sales/collections", icon: CreditCard, label: "Collections", testid: "nav-sales-collections" },
  { to: "/sales/reports", icon: BarChart3, label: "Reports", testid: "nav-sales-reports" },
];

const salesManagerOnlyLinks = [
  { to: "/sales/team", icon: UserCog, label: "Sales team", testid: "nav-sales-team" },
];

const salesExecLinks = [
  { to: "/sales/expenses", icon: CreditCard, label: "Expenses", testid: "nav-sales-expenses" },
  { to: "/sales/leave", icon: Inbox, label: "Leave", testid: "nav-sales-leave" },
];

const dealerLinks = [
  { to: "/dealer", icon: LayoutDashboard, label: "Dashboard", testid: "nav-dealer-dashboard", end: true },
  { to: "/dealer/products", icon: ShoppingBag, label: "Products", testid: "nav-dealer-products" },
  { to: "/dealer/cart", icon: ShoppingCart, label: "Cart", testid: "nav-dealer-cart" },
  { to: "/dealer/wishlist", icon: BookOpen, label: "Wishlist", testid: "nav-dealer-wishlist" },
  { to: "/dealer/quote-requests", icon: FileText, label: "Quotation requests", testid: "nav-dealer-quotes" },
  { to: "/dealer/orders", icon: PackageCheck, label: "Orders & tracking", testid: "nav-dealer-orders" },
  { to: "/dealer/loyalty", icon: Sparkles, label: "Loyalty", testid: "nav-dealer-loyalty" },
  { to: "/dealer/returns", icon: Inbox, label: "Returns & claims", testid: "nav-dealer-returns" },
  { to: "/dealer/support", icon: LifeBuoy, label: "Support", testid: "nav-dealer-support" },
  { to: "/dealer/profile", icon: UserCog, label: "Profile", testid: "nav-dealer-profile" },
];

const distributorLinks = [
  { to: "/distributor", icon: LayoutDashboard, label: "Dashboard", testid: "nav-dist-dashboard", end: true },
  { to: "/distributor/branding", icon: Palette, label: "Profile & Brand", testid: "nav-dist-branding" },
  { to: "/distributor/catalog", icon: Boxes, label: "Catalog", testid: "nav-dist-catalog" },
  { to: "/distributor/inventory/products", icon: Boxes, label: "Products", testid: "nav-dist-products" },
  { to: "/distributor/inventory/stock", icon: Factory, label: "Stock & warehouse", testid: "nav-dist-stock" },
  { to: "/distributor/bulk-orders/po", icon: ShoppingCart, label: "Purchase orders", testid: "nav-dist-po" },
  { to: "/distributor/bulk-orders/grn", icon: PackageCheck, label: "GRN", testid: "nav-dist-grn" },
  { to: "/distributor/dispatch", icon: Truck, label: "Dispatch", testid: "nav-dist-dispatch" },
  { to: "/distributor/pricing", icon: Tag, label: "Pricing & credit", testid: "nav-dist-pricing" },
  { to: "/distributor/customers", icon: Users, label: "Customers", testid: "nav-dist-customers" },
  { to: "/distributor/vendor-plants", icon: Factory, label: "Vendor plants", testid: "nav-dist-vendor-plants" },
  { to: "/distributor/logistics", icon: Truck, label: "Logistics", testid: "nav-dist-logistics" },
  { to: "/distributor/payments", icon: CreditCard, label: "Payments", testid: "nav-dist-payments" },
  { to: "/distributor/collections", icon: Wallet, label: "Collections", testid: "nav-dist-collections" },
  { to: "/distributor/bi", icon: BarChart3, label: "Regional BI", testid: "nav-dist-bi" },
  { to: "/distributor/team", icon: UserPlus, label: "Team & staff", testid: "nav-dist-team" },
  { to: "/distributor/audit", icon: Database, label: "Audit log", testid: "nav-dist-audit" },
];

const warehouseLinks = [
  { to: "/warehouse", icon: LayoutDashboard, label: "Dashboard", testid: "nav-wh-dashboard", end: true },
  { to: "/warehouse/inventory", icon: Boxes, label: "Inventory", testid: "nav-wh-inventory" },
  { to: "/warehouse/bins", icon: Layers, label: "Bins & locations", testid: "nav-wh-bins" },
  { to: "/warehouse/grn", icon: PackageOpen, label: "GRN — receiving", testid: "nav-wh-grn" },
  { to: "/warehouse/picking", icon: ClipboardList, label: "Picking", testid: "nav-wh-picking" },
  { to: "/warehouse/packing", icon: PackageCheck, label: "Packing", testid: "nav-wh-packing" },
  { to: "/warehouse/dispatch", icon: Truck, label: "Dispatch", testid: "nav-wh-dispatch" },
  { to: "/warehouse/transfers", icon: ArrowRightLeft, label: "Transfers", testid: "nav-wh-transfers" },
  { to: "/warehouse/audits", icon: ShieldCheck, label: "Cycle count", testid: "nav-wh-audits" },
  { to: "/warehouse/equipment", icon: Wrench, label: "Equipment", testid: "nav-wh-equipment" },
  { to: "/warehouse/barcode", icon: ScanBarcode, label: "Barcode scan", testid: "nav-wh-barcode" },
];

// Per-role scoped sidebar for Org sub-roles.
// ORG_ADMIN sees all links; others see only the pages relevant to their function.
const ROLE_NAV_SCOPE = {
  FINANCE_MANAGER:      ["/org", "/org/finance", "/org/customers", "/org/vendors", "/org/approvals"],
  ACCOUNTS_EXECUTIVE:   ["/org", "/org/finance", "/org/customers", "/org/vendors"],
  PROCUREMENT_MANAGER:  ["/org", "/org/purchase", "/org/vendors", "/org/stock", "/org/approvals"],
  PURCHASE_MANAGER:     ["/org", "/org/purchase", "/org/vendors", "/org/stock", "/org/approvals"],
  PURCHASE_EXECUTIVE:   ["/org", "/org/purchase", "/org/vendors"],
  INVENTORY_MANAGER:    ["/org", "/org/stock", "/org/qc"],
  STORE_MANAGER:        ["/org", "/org/stock"],
  WAREHOUSE_MANAGER:    ["/org", "/org/stock", "/org/dispatch"],
  PRODUCTION_MANAGER:   ["/org", "/org/production", "/org/qc", "/org/stock"],
  QC_MANAGER:           ["/org", "/org/qc", "/org/production"],
  SALES_MANAGER:        ["/org", "/org/customers", "/org/production", "/org/dispatch"],
  SALES_MARKETING_MANAGER: ["/org", "/org/customers", "/org/production", "/org/dispatch"],
  DISPATCH_MANAGER:     ["/org", "/org/dispatch", "/org/customers"],
  DISPATCH_EXECUTIVE:   ["/org", "/org/dispatch"],
  HR_MANAGER:           ["/org"],
};

export default function AppShell({ children, variant }) {
  const { t } = useTranslation();
  const { user, tenant, logout } = useAuth();
  const { plan } = usePlan();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const location = useLocation();

  // Close mobile drawer on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Auto-open Change Password dialog once per session for users flagged must_change_password.
  useEffect(() => {
    if (user?.must_change_password && !sessionStorage.getItem("cera_pw_dismissed_" + user.id)) {
      setPwOpen(true);
    }
  }, [user?.id, user?.must_change_password]);

  let links = orgLinks;
  if (variant === "admin") links = adminLinks;
  else if (variant === "buyer") links = buyerLinks;
  else if (variant === "vendor") links = vendorLinks;
  else if (variant === "dealer") links = dealerLinks;
  else if (variant === "distributor") links = distributorLinks;
  else if (variant === "warehouse") links = warehouseLinks;
  else if (variant === "sales") {
    links = [...salesLinks];
    if (user?.role === "SALES_MANAGER" || user?.role === "ORG_ADMIN") {
      links.splice(8, 0, ...salesManagerOnlyLinks); // insert "Sales team" after Inventory
    }
    if (user?.role === "SALES_EXECUTIVE" || user?.role === "SALES_COORDINATOR") {
      links.push(...salesExecLinks);
    }
  }

  // Filter org sidebar by plan's enabled_modules
  if (variant === "org" && plan && Array.isArray(plan.enabled_modules)) {
    const enabledSet = new Set(plan.enabled_modules);
    links = orgLinks.filter((l) => !l.modules || l.modules.some((m) => enabledSet.has(m)));
  }

  // Per-role scoped sidebar filter (ROLE_NAV_SCOPE at module scope)
  if (variant === "org" && user?.role && user.role !== "ORG_ADMIN" && ROLE_NAV_SCOPE[user.role]) {
    const allowed = new Set(ROLE_NAV_SCOPE[user.role]);
    links = links.filter((l) => allowed.has(l.to));
  }

  // CA role sees ONLY CA Audit page in the sidebar
  if (variant === "org" && user?.role === "CA") {
    links = [{ to: "/org/audit-ca", icon: ScrollText, label: "CA Audit", testid: "nav-ca-audit-ca", end: true }];
  }

  const basePath = variant === "admin" ? "/admin" : variant === "vendor" ? "/vendor" : variant === "buyer" ? "/buyer" : variant === "sales" ? "/sales" : variant === "dealer" ? "/dealer" : variant === "distributor" ? "/distributor" : variant === "warehouse" ? "/warehouse" : "/org";

  return (
    <div className="min-h-screen flex bg-[#f5f5f0]">
      {/* Mobile backdrop */}
      {mobileOpen ? (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setMobileOpen(false)}
          data-testid="mobile-backdrop"
        />
      ) : null}

      <aside
        className={`fixed lg:relative z-50 h-full w-64 shrink-0 bg-[#0a0a14] text-white border-r border-white/10 flex flex-col overflow-hidden transition-transform duration-300 ease-in-out ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
        data-testid="app-sidebar"
      >
        {/* Ambient glow accents */}
        <div className="absolute -top-20 -left-20 w-60 h-60 rounded-full bg-orange-500/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -right-20 w-60 h-60 rounded-full bg-fuchsia-600/10 blur-3xl pointer-events-none" />

        <div className="relative px-5 py-5 border-b border-white/10">
          {tenant?.branding?.logo_url ? (
            <div className="flex items-center gap-3" data-testid="tenant-logo">
              <img src={resolveLogo(tenant.branding.logo_url)} alt="logo" className="w-9 h-9 rounded-md object-cover border border-white/15" />
              <div className="flex flex-col leading-tight min-w-0">
                <span className="font-display font-extrabold text-sm truncate text-white">{tenant.branding.company_name || tenant.name}</span>
                <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">on TileSphere</span>
              </div>
            </div>
          ) : (
            <TileSphereLogo />
          )}
        </div>

        <nav className="relative flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          <div className="text-[10px] uppercase tracking-[0.25em] text-orange-300/80 font-bold px-3 py-2">
            {variant === "admin" ? t("nav.groups.platformConsole") : variant === "buyer" ? t("nav.groups.buyer") : variant === "vendor" ? t("nav.groups.vendor") : variant === "sales" ? t("nav.groups.sales") : variant === "dealer" ? t("nav.groups.dealer") : variant === "distributor" ? t("nav.groups.distributor") : variant === "warehouse" ? t("nav.groups.warehouse") : t("nav.groups.organization")}
          </div>
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={l.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                  isActive
                    ? "bg-gradient-to-r from-orange-500/20 to-pink-500/10 text-white border border-orange-500/30 shadow-lg shadow-orange-500/10"
                    : "text-white/60 hover:bg-white/5 hover:text-white border border-transparent"
                }`}
            >
              <l.icon className="w-4 h-4" />
              {l.labelKey ? t(l.labelKey, l.label) : l.label}
            </NavLink>
          ))}

          {variant === "org" && user?.role === "ORG_ADMIN" && (
            <>
              <div className="text-[10px] uppercase tracking-[0.25em] text-orange-300/80 font-bold px-3 pt-6 pb-2">{t("nav.groups.admin")}</div>
              {orgAdminLinks.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={l.testid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                      isActive
                        ? "bg-gradient-to-r from-orange-500/20 to-pink-500/10 text-white border border-orange-500/30"
                        : "text-white/60 hover:bg-white/5 hover:text-white border border-transparent"
                    }`}
                >
                  <l.icon className="w-4 h-4" />
                  {l.labelKey ? t(l.labelKey, l.label) : l.label}
                </NavLink>
              ))}
            </>
          )}

          <div className="text-[10px] uppercase tracking-[0.25em] text-orange-300/80 font-bold px-3 pt-6 pb-2">{t("nav.groups.settings")}</div>
          <NavLink to={`${basePath}/settings/mfa`} data-testid="nav-mfa"
            className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all ${isActive ? "bg-gradient-to-r from-orange-500/20 to-pink-500/10 text-white border border-orange-500/30" : "text-white/60 hover:bg-white/5 hover:text-white border border-transparent"}`}>
            <ShieldCheck className="w-4 h-4" /> {t("common.twoFactor")}
          </NavLink>
          <NavLink to={`${basePath}/audit`} data-testid="nav-audit"
            className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all ${isActive ? "bg-gradient-to-r from-orange-500/20 to-pink-500/10 text-white border border-orange-500/30" : "text-white/60 hover:bg-white/5 hover:text-white border border-transparent"}`}>
            <BookOpen className="w-4 h-4" /> {t("common.auditLog")}
          </NavLink>
        </nav>

        <div className="relative border-t border-white/10 p-4">
          {variant === "org" && plan?.plan_name ? (
            <div className="mb-3 p-2.5 rounded-xl bg-gradient-to-br from-orange-500/10 to-pink-500/5 border border-white/10" data-testid="plan-badge">
              <div className="flex items-center justify-between">
                <span className="text-[9px] uppercase tracking-[0.25em] text-orange-300 font-bold">{t("common.plan")}</span>
                <span className="text-[9px] font-mono text-white/50">{t("common.daysLeft", { days: plan.days_remaining })}</span>
              </div>
              <div className="font-display text-sm font-black text-white mt-0.5">{plan.plan_name}</div>
            </div>
          ) : null}
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-orange-400 to-pink-500 text-white grid place-items-center font-display font-bold shadow-lg">
              {(user?.full_name || user?.email || "U").slice(0, 1).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate text-white" data-testid="user-name">{user?.full_name}</div>
              <div className="text-xs text-white/50 truncate">{user?.email}</div>
              <div className="text-[10px] font-mono text-orange-300/80 mt-0.5">{user?.role}</div>
            </div>
          </div>
          <button
            onClick={() => setPwOpen(true)}
            className="w-full mb-2 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-white/15 hover:bg-white/10 text-sm font-medium text-white/80 hover:text-white transition-colors"
            data-testid="change-password-button"
          >
            <KeyRound className="w-4 h-4" /> {t("common.changePassword")}
          </button>
          <button
            onClick={async () => { const p = await logout(); navigate(p || "/login"); }}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-white/15 hover:bg-white/10 text-sm font-medium text-white/80 hover:text-white transition-colors"
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4" /> {t("common.signOut")}
          </button>
        </div>
      </aside>

      <ChangePasswordDialog
        open={pwOpen}
        mustChange={!!user?.must_change_password}
        onClose={(saved) => {
          setPwOpen(false);
          if (saved) {
            try { sessionStorage.setItem("cera_pw_dismissed_" + user.id, "1"); } catch { /* noop */ }
          }
        }}
      />

      <main className="flex-1 min-w-0 flex flex-col w-full">
        <header className="h-14 px-4 sm:px-6 border-b border-border bg-white/80 backdrop-blur-xl flex items-center gap-3 sticky top-0 z-30">
          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(true)}
            className="lg:hidden p-2 -ml-2 rounded-md hover:bg-black/5 text-foreground"
            data-testid="mobile-menu-btn"
            aria-label="Open navigation"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex-1" />
          <LanguageSwitcher variant="light" />
          <NotificationBell />
        </header>
        <div className="flex-1 overflow-x-hidden" data-scroll-region="main">{children}</div>
      </main>
    </div>
  );
}
